<template>
  <section class="border-t border-gray-200 px-4 py-4" aria-label="Aircraft index administration">
    <div class="flex flex-wrap items-center gap-2">
      <div>
        <h2 class="text-sm font-bold text-primary">Aircraft index administration</h2>
        <p class="text-xs text-gray-500">Maintain individual records or preview a bulk upsert.</p>
      </div>
      <span class="flex-grow"></span>
      <button type="button" class="!m-0" @click="beginNew">Add aircraft</button>
      <button v-if="selected" type="button" class="!m-0" @click="beginEdit">Edit selected</button>
      <button v-if="selected" type="button" class="!m-0 !border-red-200 !text-red-700" :disabled="busy" @click="removeSelected">
        Delete selected
      </button>
    </div>

    <form v-if="editorOpen" class="mt-4 border-t border-gray-100 pt-4" @submit.prevent="save">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <label v-for="field in FIELDS" :key="field.key" class="block min-w-0 text-xs font-bold text-gray-600">
          {{ field.label }}
          <input
            v-model="draft[field.key]"
            :type="field.key === 'tcds_link' ? 'url' : 'text'"
            class="mt-1 w-full border-b-2 border-gray-200 bg-transparent py-2 text-sm font-normal outline-none focus:border-primary"
            required
          />
        </label>
      </div>
      <div class="mt-4 flex justify-end gap-2">
        <button type="button" class="!m-0" :disabled="busy" @click="editorOpen = false">Cancel</button>
        <button type="submit" class="primary !m-0" :disabled="busy">
          {{ busy ? "Saving..." : "Save aircraft" }}
        </button>
      </div>
    </form>

    <div class="mt-4 border-t border-gray-100 pt-4">
      <div class="flex flex-wrap items-end gap-3">
        <label class="block min-w-0 flex-1 text-xs font-bold text-gray-600">
          CSV or JSON import
          <input
            type="file"
            accept=".csv,.json,text/csv,application/json"
            class="mt-1 block w-full text-sm font-normal"
            @change="loadFile"
          />
        </label>
        <button type="button" class="!m-0" :disabled="!importRows.length || busy" @click="previewImport">
          {{ previewing ? "Previewing..." : "Preview import" }}
        </button>
        <button type="button" class="primary !m-0" :disabled="!canCommit || busy" @click="commitImport">
          {{ committing ? "Importing..." : "Commit upsert" }}
        </button>
      </div>
      <p class="mt-2 text-xs text-gray-500">
        Required headers: {{ FIELD_KEYS.join(", ") }}. Optional: _id. Imports create or update only.
      </p>
      <p v-if="importError" class="mt-2 text-xs text-red-700">{{ importError }}</p>

      <div v-if="previewResults.length" class="mt-3 overflow-hidden rounded border border-gray-200">
        <div class="flex flex-wrap gap-4 bg-gray-50 px-3 py-2 text-xs font-bold text-gray-600">
          <span v-for="(count, status) in previewSummary" :key="status">{{ status }}: {{ count }}</span>
        </div>
        <div v-if="previewIssues.length" class="max-h-48 overflow-y-auto border-t border-gray-200">
          <div v-for="issue in previewIssues" :key="`${issue.row}-${issue.status}`" class="border-b border-gray-100 px-3 py-2 text-xs">
            <strong>Row {{ issue.row + 2 }}:</strong> {{ issue.status }} - {{ issue.message }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import Papa from "papaparse";
import { computed, reactive, ref, watch } from "vue";
import { useToast } from "vue-toastification";

import { bulkUpsertAircraft, deleteAircraft, upsertAircraft } from "../api.js";

const props = defineProps({ selected: { type: Object, default: null } });
const emit = defineEmits(["deleted", "imported", "saved"]);
const toast = useToast();

const FIELDS = [
  { key: "manufacturer", label: "Manufacturer" },
  { key: "model", label: "Model" },
  { key: "variant", label: "Variant" },
  { key: "ac_type", label: "Aircraft type" },
  { key: "cs", label: "Certification specification" },
  { key: "country", label: "Country" },
  { key: "tcds_ref", label: "TCDS reference" },
  { key: "tcds_link", label: "TCDS link" },
];
const FIELD_KEYS = FIELDS.map((field) => field.key);
const BATCH_SIZE = 250;
const draft = reactive(Object.fromEntries(FIELD_KEYS.map((key) => [key, ""])));
const editingId = ref(null);
const editorOpen = ref(false);
const busy = ref(false);
const previewing = ref(false);
const committing = ref(false);
const importRows = ref([]);
const previewResults = ref([]);
const importError = ref("");

const previewSummary = computed(() => previewResults.value.reduce((summary, result) => {
  summary[result.status] = (summary[result.status] || 0) + 1;
  return summary;
}, {}));
const previewIssues = computed(() => previewResults.value.filter((result) =>
  ["invalid", "ambiguous", "failed"].includes(result.status)
));
const canCommit = computed(() => previewResults.value.length === importRows.value.length
  && previewIssues.value.length === 0
  && !busy.value);

watch(() => props.selected, () => {
  if (editorOpen.value && editingId.value) beginEdit();
});

function resetDraft(record = {}) {
  FIELD_KEYS.forEach((key) => { draft[key] = record[key] || ""; });
}

function beginNew() {
  editingId.value = null;
  resetDraft();
  editorOpen.value = true;
}

function beginEdit() {
  if (!props.selected) return;
  editingId.value = props.selected._id;
  resetDraft(props.selected);
  editorOpen.value = true;
}

async function save() {
  busy.value = true;
  try {
    const aircraft = { ...draft };
    if (editingId.value) aircraft._id = editingId.value;
    const response = await upsertAircraft({ aircraft });
    const saved = { ...response.data.record, _id: response.data.id };
    emit("saved", saved);
    editorOpen.value = false;
    toast.success("Aircraft saved.");
  } catch (error) {
    toast.error(error?.message || "Unable to save aircraft.");
  } finally {
    busy.value = false;
  }
}

async function removeSelected() {
  if (!props.selected?._id || !window.confirm(`Delete ${props.selected.manufacturer} ${props.selected.variant}?`)) return;
  busy.value = true;
  try {
    await deleteAircraft({ id: props.selected._id });
    emit("deleted", props.selected._id);
    toast.success("Aircraft deleted.");
  } catch (error) {
    toast.error(error?.message || "Unable to delete aircraft.");
  } finally {
    busy.value = false;
  }
}

function normalizeRows(rows) {
  if (!Array.isArray(rows)) throw new Error("Import data must be an array of aircraft records.");
  return rows.map((row) => Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [String(key).trim().toLowerCase(), value])
  ));
}

function validateHeaders(rows) {
  if (!rows.length) throw new Error("The import file contains no aircraft records.");
  const missing = FIELD_KEYS.filter((key) => !Object.hasOwn(rows[0], key));
  if (missing.length) throw new Error(`Missing required headers: ${missing.join(", ")}.`);
}

async function loadFile(event) {
  importRows.value = [];
  previewResults.value = [];
  importError.value = "";
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    const text = await file.text();
    let rows;
    if (file.name.toLowerCase().endsWith(".json")) {
      const parsed = JSON.parse(text);
      rows = normalizeRows(Array.isArray(parsed) ? parsed : parsed.data);
    } else {
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      if (parsed.errors.length) throw new Error(parsed.errors[0].message);
      rows = normalizeRows(parsed.data);
    }
    validateHeaders(rows);
    importRows.value = rows;
  } catch (error) {
    importError.value = error?.message || "Unable to read the import file.";
  }
}

async function processBatches(dryRun) {
  const results = [];
  for (let offset = 0; offset < importRows.value.length; offset += BATCH_SIZE) {
    const records = importRows.value.slice(offset, offset + BATCH_SIZE);
    const response = await bulkUpsertAircraft({ records, dryRun });
    results.push(...response.data.results.map((result) => ({ ...result, row: result.row + offset })));
  }
  return results;
}

async function previewImport() {
  previewing.value = true;
  busy.value = true;
  importError.value = "";
  try {
    previewResults.value = await processBatches(true);
  } catch (error) {
    importError.value = error?.message || "Unable to preview the import.";
  } finally {
    previewing.value = false;
    busy.value = false;
  }
}

async function commitImport() {
  committing.value = true;
  busy.value = true;
  importError.value = "";
  try {
    previewResults.value = await processBatches(false);
    if (previewIssues.value.length) {
      importError.value = "The import completed with failures. Review the rows below before retrying.";
      return;
    }
    toast.success(`Imported ${importRows.value.length} aircraft records.`);
    emit("imported");
  } catch (error) {
    importError.value = error?.message || "Unable to commit the import.";
  } finally {
    committing.value = false;
    busy.value = false;
  }
}
</script>
